<script setup>
  /**
   * BcstLessonBadge — shared status pill for BCS Transform lesson/quiz cards.
   *
   * Single source for every lesson/quiz status badge (Complete, Passed, Not Passed,
   * In Progress, Not Started, Locked) so the same styling is reused everywhere a
   * lesson or quiz is rendered (dashboard, category detail, search results, lesson
   * detail) instead of being duplicated per component.
   *
   * The variant fully drives both the pill colour and the icon (each state owns its
   * SVG); the label (default slot) is supplied by the caller so one colour can carry
   * different copy (e.g. "Complete" vs "Passed" both use the success variant).
   */
  import { computed } from 'vue'
  import checkGreenIcon from '@/assets/images/bcst-check-green.svg'
  import xCircleIcon from '@/assets/images/bcst-x-circle.svg'
  import lockBadgeIcon from '@/assets/images/bcst-lock-quiz-badge.svg'
  import calendarIcon from '@/assets/images/bcst-continue-card-category.svg'

  const props = defineProps({
    // Pill colour + icon variant.
    variant: {
      type: String,
      required: true,
      validator: (v) =>
        ['success', 'danger', 'in-progress', 'not-started', 'locked', 'next-in-queue'].includes(v),
    },
  })

  // Each variant owns its icon (or none) — no per-call-site icon override, so the badge
  // always renders the icon that matches its state.
  const ICON_BY_VARIANT = {
    success: checkGreenIcon,
    danger: xCircleIcon,
    locked: lockBadgeIcon,
    'next-in-queue': calendarIcon,
  }
  const svgIcon = computed(() => ICON_BY_VARIANT[props.variant] ?? null)
</script>

<template>
  <span class="bcst-badge" :class="`bcst-badge--${variant}`">
    <img v-if="svgIcon" :src="svgIcon" alt="" class="bcst-badge-svg-icon" /><slot />
  </span>
</template>

<style scoped lang="scss">
  .bcst-badge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 4px 10px;
    border-radius: 100px;
    font-size: 0.875rem;
    font-weight: 700;
    letter-spacing: -0.28px;
    line-height: 1.3;
  }

  .bcst-badge--success,
  .bcst-badge--next-in-queue {
    background-color: var(--green-060);
    color: var(--green-500);
  }

  .bcst-badge--danger {
    background-color: var(--error-050);
    color: var(--error-500);
  }

  .bcst-badge--in-progress {
    background-color: #fff3cd;
    color: var(--Warning-Text, #D88201);
    text-align: center;
    font-family: "DM Sans";
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: 130%; /* 18.2px */
    letter-spacing: -0.28px;
  }

  .bcst-badge--not-started {
    background-color: var(--error-050);  /* #FFEBED */
    color: var(--error-500);             /* #DC3545 */
  }

  /* Locked: neutral grey reads as "unavailable" and never competes with the
     status colours (success/danger) that convey completion state. */
  .bcst-badge--locked {
    background-color: var(--bs-gray-200);
    color: var(--Gray-600, #6C757D);
    text-align: center;
    font-family: "DM Sans";
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: 130%; /* 18.2px */
    letter-spacing: -0.28px;
  }

  .bcst-badge--locked .bcst-badge-svg-icon {
    width: 16px;
    height: 16px;
  }

  .bcst-badge-svg-icon {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
</style>
